import { useEffect, useMemo } from "react";
import { useLocation, useNavigationType, useParams, useSearchParams } from "react-router-dom";

import { type JobPostFilters } from "@av/apps/recruitment/ats-common/types";
import { FilterSearchParamMapper } from "./search-params-mapper";

// Where a history entry came from, kept in its router state so it survives a reload. "board" entries
// save their list state. "link" entries come from a plain job link opened from outside the board, and
// the entries that follow it without a list change from the user; they leave the saved state alone.
type JobBoardEntry = "board" | "link";
type JobBoardEntryState = { jobBoardEntry?: JobBoardEntry } | null;

export function jobBoardEntryState(entry: JobBoardEntry) {
  return { jobBoardEntry: entry };
}

// sessionStorage keeps the list state per browser tab, so closing the tab clears it. Each company
// has its own entry. When storage is unavailable the board keeps working from the URL alone.
const listStateStorage = {
  key: (companyId: string) => `public-job-board-list-state:${companyId}`,
  read(companyId: string) {
    try {
      return sessionStorage.getItem(this.key(companyId));
    } catch {
      return null;
    }
  },
  write(companyId: string, mapper: FilterSearchParamMapper) {
    try {
      sessionStorage.setItem(this.key(companyId), mapper.listSearchParams.toString());
    } catch {
      // The URL still holds the list state.
    }
  },
};

/**
 * The URL query stays the source of truth for the job board list. The list state is also saved
 * for the tab so entering a company's board without list params restores its previous list state.
 */
export function useJobBoardFilterParams(companyId: string | undefined) {
  const [urlSearchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const navigationType = useNavigationType();
  const { jobPostId } = useParams();
  const urlSearchParamsString = urlSearchParams.toString();
  const entryFromState = (state as JobBoardEntryState)?.jobBoardEntry;
  const hasListSearchParams = FilterSearchParamMapper.hasListSearchParams(urlSearchParams);

  // Going back or forward to an entry the board marked, or reloading it, applies its own query, even
  // an empty one, instead of the latest saved list state.
  let entry: JobBoardEntry = entryFromState ?? "board";

  if (navigationType === "POP" && entryFromState) {
    entry = "board";
  } else if (!entryFromState && jobPostId && !hasListSearchParams) {
    entry = "link";
  }

  // An entry with a list query applies it on every visit, so only entries without one need a mark
  const isEntryMarked = entryFromState === entry || (!entryFromState && hasListSearchParams);
  const isNewBoardEntryWithoutListState = !entryFromState && !jobPostId && !hasListSearchParams;
  const savedListState =
    companyId && isNewBoardEntryWithoutListState ? listStateStorage.read(companyId) : null;

  const restoredMapper = useMemo(() => {
    if (!savedListState) {
      return undefined;
    }

    const nextMapper = new FilterSearchParamMapper(urlSearchParamsString);

    nextMapper.applyListState(savedListState);

    return nextMapper.listSearchParams.toString() === "" ? undefined : nextMapper;
  }, [savedListState, urlSearchParamsString]);
  const urlMapper = useMemo(
    () => new FilterSearchParamMapper(urlSearchParamsString),
    [urlSearchParamsString]
  );
  // The restored values show right away, so the page never acts on the default list first. The
  // URL holds them after the replace below.
  const mapper = restoredMapper ?? urlMapper;

  useEffect(() => {
    if (restoredMapper) {
      setSearchParams(restoredMapper.urlSearchParams, { replace: true });

      return;
    }

    if (companyId && entry === "board") {
      listStateStorage.write(companyId, urlMapper);
    }

    // Marks the entry, so a later return or reload knows where it came from. The page may still
    // show this location for a while after the replace, so the save above does not wait for it.
    if (!isEntryMarked || urlMapper.urlSearchParams.toString() !== urlSearchParamsString) {
      setSearchParams(urlMapper.urlSearchParams, {
        replace: true,
        state: jobBoardEntryState(entry),
      });
    }
  }, [
    companyId,
    entry,
    isEntryMarked,
    restoredMapper,
    setSearchParams,
    urlMapper,
    urlSearchParamsString,
  ]);

  // Save before the URL changes so an emptied list state is never restored from an older save.
  const applyMapper = (nextMapper: FilterSearchParamMapper) => {
    if (companyId) {
      listStateStorage.write(companyId, nextMapper);
    }

    setSearchParams(nextMapper.urlSearchParams, { state: jobBoardEntryState("board") });
  };

  // The page follows the shown job, so a "link" entry keeps leaving the saved state alone.
  const setPage = (newPage: number) => {
    const newMapper = mapper.clone();

    newMapper.setPage(newPage);

    if (mapper.isFiltersEqual(newMapper)) {
      return;
    }

    if (entry === "link") {
      setSearchParams(newMapper.urlSearchParams, { state: jobBoardEntryState("link") });

      return;
    }

    applyMapper(newMapper);
  };

  /** Returns whether the page was kept because filters were only cleared */
  const setFilters = (newFilter: JobPostFilters) => {
    const newSearchParamsMapper = mapper.clone();

    newSearchParamsMapper.applyFilter(newFilter);

    // Clearing a filter resets only that filter, so the page stays. Any other change starts over.
    const isFilterRemoval = mapper.isFilterRemoval(newSearchParamsMapper);

    if (isFilterRemoval) {
      newSearchParamsMapper.applyFilter({ ...newFilter, page: mapper.toFilters().page });
    }

    if (mapper.isFiltersEqual(newSearchParamsMapper)) {
      return false;
    }

    applyMapper(newSearchParamsMapper);

    return isFilterRemoval;
  };

  return {
    filters: mapper.toFilters(),
    searchParamsString: mapper.urlSearchParams.toString(),
    filtersActive: mapper.hasActiveFilters(),
    restoredFromSavedState: Boolean(restoredMapper),
    setPage,
    setFilters,
  } as const;
}
